import { LogUserActivity } from '@/apis/users/requests'
import { SESSION_STORAGE_KEYS } from '@/app/common/session-storage'
import { Button } from '@/common/components/Button.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import CheckmarkIcon from '@assets/check-circle.svg'
import UserCircle from '@assets/user-circle.svg'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LOG_TYPES } from '../constants'

const ResidencyDocsVerify = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Two documents required for Residency page',
      })
      hasLoggedPageView.current = true;
    }
  }, []) 

  function getNumberOfUploadedDocuments(): number {
    const numberOfUploadedResidencyDocuments = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.numberOfUploadedResidencyDocuments
    )

    if (!numberOfUploadedResidencyDocuments) {
      return 0
    }

    const asNumber = parseInt(numberOfUploadedResidencyDocuments, 10)

    if (isNaN(asNumber)) {
      throw new Error('Failed to parse number of uploaded residency documents')
    }

    if (!asNumber) {
      return 0
    }

    return asNumber
  }

  const hasFirstDocument = getNumberOfUploadedDocuments() >= 1
  const hasSecondDocument = getNumberOfUploadedDocuments() >= 2

  const handleVerifyClick = () => {
    navigate(
      AppRoutePaths.family_application.residency_verification
        .alternative_docs_capture
    )
  }

  return (
    <>
      <h1
        id="income-verification-title"
        className="text-xl text-center font-medium text-gray-900 mb-10 sm:mb-5"
      >
        {translate('residency_docs.two_documents_required')}
      </h1>

      <div className="flex flex-col gap-4">
        <article className="flex flex-col items-center justify-center border border-gray-200 rounded-xl align-middle pt-3 pr-5 pb-4 pl-5">
          <img
            src={hasFirstDocument ? CheckmarkIcon : UserCircle}
            alt={translate('user_icon')}
            className="h-9 mb-1"
            aria-hidden="true"
          />
          <div className="flex flex-col items-center justify-center">
            <h2 className="text-center text-md leading-6 font-medium">
              {translate('first_document')}
            </h2>
          </div>
          {!hasFirstDocument && (
            <Button
              onClick={() => handleVerifyClick()}
              className="mt-4 text-gray-900 w-full rounded text-md leading-6 font-semibold"
            >
              {translate('upload_document')}
            </Button>
          )}
        </article>

        <article className="flex flex-col items-center justify-center border border-gray-200 rounded-xl align-middle pt-3 pr-5 pb-4 pl-5">
          <img
            src={hasSecondDocument ? CheckmarkIcon : UserCircle}
            alt={translate('user_icon')}
            className="h-9 mb-1 mt-0"
            aria-hidden="true"
          />
          <div className="flex flex-col items-center justify-center">
            <h2 className="text-center text-md leading-6 font-medium">
              {translate('second_document')}
            </h2>
          </div>
          {!hasSecondDocument && (
            <Button
              onClick={() => handleVerifyClick()}
              className="mt-4 text-gray-900 w-full rounded text-md leading-6 font-semibold"
            >
              {translate('upload_document')}
            </Button>
          )}
        </article>
      </div>
      <div className="text-center h-full flex">
        <div className="flex justify-end my-5 w-full flex-col">
          {hasFirstDocument && hasSecondDocument && (
            <Button
              onClick={() =>
                navigate(
                  AppRoutePaths.family_application.residency_verification
                    .enter_details
                )
              }
              className="rounded h-9"
            >
              {translate('continue')}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
export default ResidencyDocsVerify;
